import { useEffect, useId, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';

/**
 * Zeitreihen-Diagramm für die Statistik (Revision R3), gebaut nach der Dataviz-Methode:
 * eine Achse, 2px-Linien bzw. Balken ≤ 24px mit 4px-Rundung am Datenende, Raster als Haarlinie, Fadenkreuz +
 * Tooltip mit allen Reihen (Maus und Pfeiltasten), Legende ab zwei Reihen, Endwerte direkt beschriftet,
 * Tabellenansicht. Farben nur über die Tokens `--series-1/2` (geprüft hell und dunkel); Text immer in Text-Tokens.
 */

export interface Series {
  key: string;
  label: string;
  /** CSS-Farbe, z. B. `var(--series-1)` */
  color: string;
  values: number[];
}

interface Props {
  title: string;
  /** Zeitpunkte (ISO), einer pro Wert */
  times: string[];
  series: Series[];
  kind: 'line' | 'bar';
  format: (v: number) => string;
  formatTime: (iso: string) => string;
  /** Format im Tooltip und in der Tabelle (ausführlicher als die Achse) */
  formatTimeLong: (iso: string) => string;
}

const H = 180;
const M = { top: 12, right: 52, bottom: 24, left: 44 };

/** Saubere Obergrenze und Schritte für die y-Achse (0, 1/2/5 × 10^n). */
function niceTicks(max: number, count = 4): number[] {
  if (max <= 0) return [0, 1];
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((f) => f * mag).find((s) => s >= raw) ?? 10 * mag;
  const ticks = [];
  for (let v = 0; v <= max + step * 0.0001; v += step) ticks.push(Math.round(v * 1e6) / 1e6);
  if (ticks.at(-1)! < max) ticks.push(ticks.at(-1)! + step);
  return ticks;
}

export const compact = (v: number) =>
  v >= 1_000_000 ? `${(v / 1_000_000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} Mio.` : v >= 1000 ? `${(v / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} Tsd.` : v.toLocaleString('de-DE');

function useWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(240, Math.floor(e!.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

export function TimeChart({ title, times, series, kind, format, formatTime, formatTimeLong }: Props) {
  const [wrapRef, width] = useWidth();
  const [hover, setHover] = useState<number | null>(null);
  const titleId = useId();
  const n = times.length;
  const plotW = width - M.left - M.right;
  const plotH = H - M.top - M.bottom;
  const max = Math.max(0, ...series.flatMap((s) => s.values));
  const ticks = niceTicks(max);
  const yMax = ticks.at(-1)!;
  const band = n > 0 ? plotW / n : plotW;
  const x = (i: number) => M.left + (kind === 'bar' ? band * i + band / 2 : n > 1 ? (plotW * i) / (n - 1) : plotW / 2);
  const y = (v: number) => M.top + plotH - (v / yMax) * plotH;

  // höchstens ~6 Zeitbeschriftungen, gleichmäßig
  const every = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(plotW / 90))));
  const xLabels = times.map((t, i) => ({ t, i })).filter(({ i }) => i % every === 0);

  const indexAt = (clientX: number, rect: DOMRect) => {
    const px = clientX - rect.left - M.left;
    const i = kind === 'bar' ? Math.floor(px / band) : Math.round((px / plotW) * (n - 1));
    return Math.min(n - 1, Math.max(0, i));
  };
  const onMove = (e: PointerEvent<SVGRectElement>) => setHover(indexAt(e.clientX, e.currentTarget.ownerSVGElement!.getBoundingClientRect()));
  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    setHover((h) => Math.min(n - 1, Math.max(0, (h ?? n - 1) + (e.key === 'ArrowRight' ? 1 : -1))));
  };

  const barW = Math.min(24, Math.max(1, band - 2) / Math.max(1, series.length));
  const barPath = (cx: number, v: number) => {
    const top = y(v);
    const h = M.top + plotH - top;
    const w = barW;
    const r = Math.min(4, w / 2, h);
    const x0 = cx - w / 2;
    return `M${x0},${M.top + plotH} V${top + r} Q${x0},${top} ${x0 + r},${top} H${x0 + w - r} Q${x0 + w},${top} ${x0 + w},${top + r} V${M.top + plotH} Z`;
  };

  const tipLeft = hover === null ? 0 : Math.min(width - 180, Math.max(0, x(hover) + 12));

  return (
    <figure className="chart">
      <figcaption id={titleId} className="chart-title">
        {title}
      </figcaption>
      {series.length > 1 && (
        <ul className="chart-legend" aria-label="Legende">
          {series.map((s) => (
            <li key={s.key}>
              <span className="chart-key" style={{ background: s.color }} aria-hidden />
              {s.label}
            </li>
          ))}
        </ul>
      )}
      <div ref={wrapRef} className="chart-plot">
        <svg
          width={width}
          height={H}
          role="img"
          aria-labelledby={titleId}
          tabIndex={0}
          onKeyDown={onKey}
          onFocus={() => setHover((h) => h ?? n - 1)}
          onBlur={() => setHover(null)}
        >
          {ticks.map((t) => (
            <g key={t}>
              <line className="chart-grid" x1={M.left} x2={M.left + plotW} y1={y(t)} y2={y(t)} />
              <text className="chart-axis" x={M.left - 6} y={y(t)} textAnchor="end" dominantBaseline="middle">
                {format(t)}
              </text>
            </g>
          ))}
          {xLabels.map(({ t, i }) => (
            <text key={t} className="chart-axis" x={x(i)} y={H - 6} textAnchor="middle">
              {formatTime(t)}
            </text>
          ))}

          {kind === 'bar' &&
            series.map((s, si) =>
              s.values.map((v, i) =>
                v > 0 ? (
                  <path
                    key={`${s.key}-${i}`}
                    d={barPath(x(i) + (si - (series.length - 1) / 2) * (barW + 2), v)}
                    fill={s.color}
                    opacity={hover === null || hover === i ? 1 : 0.55}
                  />
                ) : null,
              ),
            )}

          {kind === 'line' &&
            series.map((s) => {
              const d = s.values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(v)}`).join(' ');
              const last = s.values.length - 1;
              return (
                <g key={s.key}>
                  {series.length === 1 && (
                    <path d={`${d} L${x(last)},${y(0)} L${x(0)},${y(0)} Z`} fill={s.color} opacity={0.1} />
                  )}
                  <path d={d} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                  {last >= 0 && (
                    <>
                      <circle cx={x(last)} cy={y(s.values[last]!)} r={4} fill={s.color} className="chart-dot" />
                      <text className="chart-end" x={x(last) + 8} y={y(s.values[last]!)} dominantBaseline="middle">
                        {format(s.values[last]!)}
                      </text>
                    </>
                  )}
                </g>
              );
            })}

          {hover !== null && (
            <g pointerEvents="none">
              <line className="chart-cross" x1={x(hover)} x2={x(hover)} y1={M.top} y2={M.top + plotH} />
              {kind === 'line' &&
                series.map((s) => <circle key={s.key} cx={x(hover)} cy={y(s.values[hover]!)} r={4} fill={s.color} className="chart-dot" />)}
            </g>
          )}
          <rect
            x={M.left}
            y={M.top}
            width={Math.max(0, plotW)}
            height={plotH}
            fill="transparent"
            onPointerMove={onMove}
            onPointerLeave={() => setHover(null)}
          />
        </svg>
        {hover !== null && (
          <div className="chart-tip" style={{ left: tipLeft }} role="status">
            <div className="chart-tip-time">{formatTimeLong(times[hover]!)}</div>
            {series.map((s) => (
              <div key={s.key} className="chart-tip-row">
                <span className="chart-key" style={{ background: s.color }} aria-hidden />
                <strong>{format(s.values[hover]!)}</strong>
                <span className="muted">{s.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <details className="chart-table">
        <summary>Als Tabelle</summary>
        <div className="table-wrap">
          <table>
            <caption className="visually-hidden">{title}</caption>
            <thead>
              <tr>
                <th scope="col">Zeit</th>
                {series.map((s) => (
                  <th key={s.key} scope="col" className="num">
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {times.map((t, i) => (
                <tr key={t}>
                  <td>{formatTimeLong(t)}</td>
                  {series.map((s) => (
                    <td key={s.key} className="num">
                      {format(s.values[i]!)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}
